/**
 * Vista «Stock de merch»: matriz producto × talla, recuento e interruptor.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { notify } from '@/lib/notify';
import { countBelowCommitted, garmentPackNames, packBaseSlug, stockTone } from './merchStockModel';
import {
    useAdjustStock,
    useAssignLotLine,
    useInitialStockCount,
    useMerchStock,
    useSetStockEnforced,
    useSetStockThreshold,
    type OversellRow,
    type StockCell,
    type StockProduct,
} from './useMerchStock';

const MOTIVOS = ['Recepción', 'Recuento', 'Merma', 'Regalo', 'Otro'];
const LOT_FIELDS = [
    { slug: 'camiseta-tecnica-corta', label: 'Manga corta' },
    { slug: 'camiseta-tecnica-larga', label: 'Manga larga' },
    { slug: 'sudadera', label: 'Sudadera' },
] as const;

function cellClass(available: number | null, threshold: number, provisional: boolean): string {
    if (available == null || provisional) return 'text-muted-foreground';
    const tone = stockTone(available, threshold);
    if (tone === 'out') return 'bg-red-500/15 text-red-300';
    if (tone === 'low') return 'bg-amber-500/15 text-amber-200';
    return '';
}

function sizeKey(size: string | null): string {
    return size ?? 'Única';
}

export function MerchStockBoard({ productId }: { productId?: string }) {
    const stock = useMerchStock();
    const setEnforced = useSetStockEnforced();
    const setThreshold = useSetStockThreshold();
    const adjust = useAdjustStock();
    const count = useInitialStockCount();
    const assign = useAssignLotLine();
    const [editing, setEditing] = useState<string | null>(null);
    const [onHand, setOnHand] = useState('');
    const [motivo, setMotivo] = useState(MOTIVOS[0]);
    const [note, setNote] = useState('');
    const [counting, setCounting] = useState(false);
    const [counted, setCounted] = useState<Record<string, string>>({});
    const [oversell, setOversell] = useState<OversellRow[] | null>(null);
    const [switchError, setSwitchError] = useState<string | null>(null);
    const [thresholdDraft, setThresholdDraft] = useState<string | null>(null);
    const [lotDraft, setLotDraft] = useState<Record<string, Record<string, string>>>({});

    if (stock.isLoading) {
        return (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando stock…
            </div>
        );
    }
    if (stock.isError || !stock.data) {
        return <p className="text-sm text-red-400">No se ha podido cargar el stock de merch.</p>;
    }

    const matrix = stock.data;
    const packNames = garmentPackNames(matrix.products);
    const focused = productId ? matrix.products.find((product) => product.id === productId) : undefined;
    const baseSlug = focused?.kind === 'pack' ? packBaseSlug(focused.slug) : null;
    const baseProduct = baseSlug ? matrix.products.find((product) => product.slug === baseSlug) : undefined;
    const products = productId
        ? (baseProduct ? [baseProduct] : matrix.products.filter((product) => product.id === productId && product.kind !== 'pack'))
        : matrix.products.filter((product) => product.kind !== 'pack');
    const sizes = [...new Set(products.flatMap((product) => product.cells.map((cell) => sizeKey(cell.size))))];
    const garments = matrix.products.filter((product) => product.kind === 'garment');
    const movements = productId
        ? matrix.movements.filter((movement) => movement.productId === productId)
        : matrix.movements;
    const thresholdText = thresholdDraft ?? String(matrix.threshold);

    const saveCell = async (cell: StockCell) => {
        const next = Number.parseInt(onHand, 10);
        if (!Number.isInteger(next) || next < 0) {
            notify({ type: 'err', text: 'El disponible tiene que ser un entero de 0 o más' });
            return;
        }
        try {
            await adjust.mutateAsync({ variantId: cell.variantId, onHand: next, motivo, note });
            setEditing(null);
            notify({ type: 'ok', text: 'Stock actualizado' });
        } catch (error: unknown) {
            notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
        }
    };

    const submitCount = async (acceptOversell: boolean) => {
        const rows = garments.flatMap((product) => product.cells.filter((cell) => cell.editable).map((cell) => {
            const raw = counted[cell.variantId];
            return { variantId: cell.variantId, counted: raw == null || raw === '' ? null : Number.parseInt(raw, 10) };
        }));
        if (rows.some((row) => row.counted == null || !Number.isInteger(row.counted) || row.counted < 0)) {
            notify({ type: 'err', text: 'Rellena las unidades contadas de cada talla' });
            return;
        }
        try {
            const result = await count.mutateAsync({
                counts: JSON.stringify(rows),
                acceptOversell,
            });
            if (!result.ok) {
                setOversell(result.oversell ?? []);
                return;
            }
            setOversell(null);
            setCounting(false);
            notify({ type: 'ok', text: acceptOversell ? 'Recuento guardado, con sobreventa a 0' : 'Recuento inicial guardado' });
        } catch (error: unknown) {
            notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
        }
    };

    return (
        <div className="space-y-6">
            {productId ? null : (
                <div className="space-y-3 rounded-xl border border-border bg-card p-4">
                    <label className="flex items-start gap-3 text-sm">
                        <input
                            type="checkbox"
                            className="mt-1"
                            checked={matrix.enforced}
                            disabled={setEnforced.isPending}
                            onChange={(event) => {
                                const next = event.target.checked;
                                setSwitchError(null);
                                if (next && !matrix.hasInitialCount) {
                                    setSwitchError('Haz el recuento inicial antes de activar el control de stock.');
                                    return;
                                }
                                void setEnforced.mutateAsync(next).catch((error: unknown) => {
                                    setSwitchError(error instanceof Error ? error.message : String(error));
                                });
                            }}
                        />
                        <span>
                            <span className="font-medium">Control de stock activo</span>
                            <span className="mt-1 block text-muted-foreground">
                                Apagado, la tienda vende como hasta ahora: no comprueba ni descuenta unidades.
                                Encendido, descuenta al cobrar y repone al cancelar.
                            </span>
                        </span>
                    </label>
                    {switchError ? <p className="text-sm text-red-400">{switchError}</p> : null}
                    {!matrix.hasInitialCount ? (
                        <p className="text-sm text-amber-200">
                            Antes de activarlo hay que hacer el recuento inicial del almacén.
                        </p>
                    ) : null}
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="stock-threshold">Avisar si queda esta cantidad o menos</Label>
                            <Input
                                id="stock-threshold"
                                inputMode="numeric"
                                className="w-24"
                                value={thresholdText}
                                onChange={(event) => setThresholdDraft(event.target.value)}
                            />
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                const next = Number.parseInt(thresholdText, 10);
                                if (!Number.isInteger(next) || next < 0) {
                                    notify({ type: 'err', text: 'El umbral no puede ser negativo' });
                                    return;
                                }
                                void setThreshold.mutateAsync(next).then(() => {
                                    setThresholdDraft(null);
                                    notify({ type: 'ok', text: 'Umbral guardado' });
                                }).catch((error: unknown) => {
                                    notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
                                });
                            }}
                        >
                            Guardar umbral
                        </Button>
                        <Button type="button" size="sm" onClick={() => { setCounting((open) => !open); setOversell(null); }}>
                            Recuento inicial
                        </Button>
                    </div>
                </div>
            )}

            {counting && !productId ? (
                <section className="space-y-3 rounded-xl border border-border p-4">
                    <h2 className="text-sm font-semibold">Unidades contadas en el almacén</h2>
                    <p className="text-sm text-muted-foreground">
                        Se guarda disponible = contado − comprometido en este momento.
                    </p>
                    {garments.map((product) => (
                        <div key={product.id} className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                            <p className="text-sm font-medium sm:col-span-2 lg:col-span-5">{product.name}</p>
                            {product.cells.filter((cell) => cell.editable).map((cell) => {
                                const raw = counted[cell.variantId] ?? '';
                                const short = countBelowCommitted(raw, cell.committed);
                                return (
                                <label key={cell.variantId} className={`space-y-1 text-xs ${short ? 'text-red-300' : ''}`}>
                                    <span>{sizeKey(cell.size)} · comprometidas {cell.committed}</span>
                                    <Input
                                        inputMode="numeric"
                                        value={raw}
                                        className={short ? 'border-red-400' : undefined}
                                        onChange={(event) => setCounted((prev) => ({
                                            ...prev,
                                            [cell.variantId]: event.target.value,
                                        }))}
                                    />
                                    {short ? (
                                        <span className="block">Por debajo de las comprometidas. Al guardar no se aplicará.</span>
                                    ) : null}
                                </label>
                                );
                            })}
                        </div>
                    ))}
                    {oversell && oversell.length > 0 ? (
                        <div className="space-y-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
                            <p>Hay tallas vendidas por encima de lo contado. No se ha guardado nada.</p>
                            {oversell.map((row) => (
                                <div key={row.variantId}>
                                    <p className="font-medium">
                                        {row.productName} {row.size ?? 'única'}: contadas {row.counted}, comprometidas {row.committed} (faltan {row.shortfall})
                                    </p>
                                    <ul className="list-disc pl-5">
                                        {row.orders.map((order) => (
                                            <li key={`${row.variantId}-${order.orderNumber}`}>
                                                {order.orderNumber} · {order.quantity} · {order.status}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                            <Button type="button" size="sm" variant="destructive" disabled={count.isPending} onClick={() => void submitCount(true)}>
                                Guardar en 0 y marcar sobreventa
                            </Button>
                        </div>
                    ) : null}
                    <div className="flex gap-2">
                        <Button type="button" size="sm" disabled={count.isPending} onClick={() => void submitCount(false)}>
                            Guardar recuento
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => { setCounting(false); setOversell(null); }}>
                            Volver
                        </Button>
                    </div>
                </section>
            ) : null}

            {focused?.kind === 'pack' && baseProduct ? (
                <p className="text-sm text-muted-foreground">
                    {focused.name} no tiene unidades propias. Las cifras son las de {baseProduct.name}.
                </p>
            ) : null}
            <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[40rem] text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2">Producto</th>
                            {sizes.map((size) => <th key={size} className="px-3 py-2">{size}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((product) => (
                            <ProductRow
                                key={product.id}
                                product={product}
                                packNames={packNames.get(product.slug) ?? []}
                                sizes={sizes}
                                threshold={matrix.threshold}
                                provisional={!matrix.enforced && !matrix.hasInitialCount}
                                editing={editing}
                                onHand={onHand}
                                motivo={motivo}
                                note={note}
                                onEdit={(cell) => {
                                    setEditing(cell.variantId);
                                    setOnHand(cell.available == null ? '' : String(cell.available));
                                }}
                                onOnHand={setOnHand}
                                onMotivo={setMotivo}
                                onNote={setNote}
                                onSave={(cell) => void saveCell(cell)}
                                onCancel={() => setEditing(null)}
                                saving={adjust.isPending}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {!productId && matrix.pendingLots.length > 0 ? (
                <section className="space-y-3">
                    <h2 className="text-sm font-semibold">Pedidos de lote con talla por asignar</h2>
                    {matrix.pendingLots.map((lot) => (
                        <div key={lot.itemId} className="space-y-2 rounded-xl border border-border p-3">
                            <p className="text-sm">
                                <Link to={`/store/orders/${lot.orderId}`} className="font-medium hover:underline">{lot.orderNumber}</Link>
                                <span className="text-muted-foreground"> · {lot.status}</span>
                            </p>
                            {lot.notes ? <p className="text-xs text-muted-foreground">Notas: {lot.notes}</p> : null}
                            <div className="flex flex-wrap items-end gap-2">
                                {LOT_FIELDS.map((field) => (
                                    <label key={field.slug} className="space-y-1 text-xs">
                                        <span>{field.label}</span>
                                        <select
                                            className="block h-9 rounded-md border border-border bg-background px-2"
                                            value={lotDraft[lot.itemId]?.[field.slug] ?? ''}
                                            onChange={(event) => setLotDraft((prev) => ({
                                                ...prev,
                                                [lot.itemId]: { ...prev[lot.itemId], [field.slug]: event.target.value },
                                            }))}
                                        >
                                            <option value="">Talla</option>
                                            {['S', 'M', 'L', 'XL', 'XXL'].map((size) => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                    </label>
                                ))}
                                <Button
                                    type="button"
                                    size="sm"
                                    disabled={assign.isPending}
                                    onClick={() => {
                                        const sizes = lotDraft[lot.itemId] ?? {};
                                        if (LOT_FIELDS.some((field) => !sizes[field.slug])) {
                                            notify({ type: 'err', text: 'Elige la talla de cada prenda' });
                                            return;
                                        }
                                        void assign.mutateAsync({ itemId: lot.itemId, sizes: JSON.stringify(sizes) })
                                            .then(() => notify({ type: 'ok', text: 'Tallas asignadas' }))
                                            .catch((error: unknown) => {
                                                notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
                                            });
                                    }}
                                >
                                    Asignar
                                </Button>
                            </div>
                        </div>
                    ))}
                </section>
            ) : null}

            <section className="space-y-2">
                <h2 className="text-sm font-semibold">Movimientos</h2>
                {movements.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Todavía no hay movimientos.</p>
                ) : (
                    <ul className="divide-y divide-border rounded-xl border border-border">
                        {movements.map((movement) => (
                            <li key={movement.id} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-sm">
                                <span>
                                    {movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}
                                    {' · '}
                                    {movement.size ?? 'única'}
                                    {' · '}
                                    {movement.motivo || movement.reason}
                                    {movement.orderNumber ? ` · ${movement.orderNumber}` : ''}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    {new Date(movement.createdAt).toLocaleString('es-ES')}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

function ProductRow({
    product,
    packNames,
    sizes,
    threshold,
    provisional,
    editing,
    onHand,
    motivo,
    note,
    onEdit,
    onOnHand,
    onMotivo,
    onNote,
    onSave,
    onCancel,
    saving,
}: {
    product: StockProduct;
    packNames: string[];
    sizes: string[];
    threshold: number;
    provisional: boolean;
    editing: string | null;
    onHand: string;
    motivo: string;
    note: string;
    onEdit: (cell: StockCell) => void;
    onOnHand: (value: string) => void;
    onMotivo: (value: string) => void;
    onNote: (value: string) => void;
    onSave: (cell: StockCell) => void;
    onCancel: () => void;
    saving: boolean;
}) {
    const bySize = new Map(product.cells.map((cell) => [sizeKey(cell.size), cell]));
    return (
        <tr className="border-t border-border align-top">
            <th className="px-3 py-3 text-left font-medium">
                {product.name}
                {packNames.length > 0 ? (
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">
                        También se vende en: {packNames.join(', ')}
                    </span>
                ) : null}
                {product.kind === 'lot' ? (
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">Descuenta cada prenda al cobrar</span>
                ) : null}
            </th>
            {sizes.map((size) => {
                const cell = bySize.get(size);
                if (!cell || cell.available == null) {
                    return <td key={size} className="px-3 py-3 text-muted-foreground">—</td>;
                }
                const open = editing === cell.variantId;
                return (
                    <td key={size} className={`px-3 py-3 ${cellClass(cell.available, threshold, provisional)}`}>
                        <div className="text-base font-semibold tabular-nums">{cell.available}</div>
                        <div className="text-[11px] text-muted-foreground">
                            total {cell.total ?? '—'} · comprometidas {cell.committed}
                        </div>
                        {cell.editable ? (
                            open ? (
                                <div className="mt-2 space-y-1">
                                    <Input inputMode="numeric" value={onHand} onChange={(event) => onOnHand(event.target.value)} />
                                    <select
                                        className="h-9 w-full rounded-md border border-border bg-background px-2 text-xs"
                                        value={motivo}
                                        onChange={(event) => onMotivo(event.target.value)}
                                    >
                                        {MOTIVOS.map((item) => <option key={item}>{item}</option>)}
                                    </select>
                                    <Input value={note} placeholder="Nota" onChange={(event) => onNote(event.target.value)} />
                                    <div className="flex gap-1">
                                        <Button type="button" size="sm" disabled={saving} onClick={() => onSave(cell)}>Guardar</Button>
                                        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Volver</Button>
                                    </div>
                                </div>
                            ) : (
                                <button type="button" className="mt-1 text-xs underline" onClick={() => onEdit(cell)}>
                                    Ajustar
                                </button>
                            )
                        ) : null}
                    </td>
                );
            })}
        </tr>
    );
}

export function MerchStockPage() {
    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-xl font-bold">Stock de merch</h1>
                <p className="text-sm text-muted-foreground">
                    Disponible en grande. Debajo, el total del almacén y las unidades comprometidas en pedidos pagados sin enviar.
                </p>
            </div>
            <MerchStockBoard />
        </div>
    );
}
